import { useMemo } from "react";
import { Button, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";

// =============================================================================
// TmCountSuggestion
//
// amount ÷ tmCount often lands on a repeating decimal (14750 / 3000 = 4.9166…).
// Payment gateways charge at most 2 decimals, so a repeating per-TM price means
// unit × qty no longer equals the amount actually charged. This component
// suggests the nearest TM counts that divide the amount into an exact 2-decimal
// per-TM price.
//
// Props:
//   amount   (number)   — target total amount in rupees
//   tmCount  (number)   — TM count currently entered by the admin
//   onSelect (function) — called with (newTmCount, perTmPrice) on click
// =============================================================================

const MAX_SUGGESTIONS = 3;

// Optional: only offer counts within ±15% of the current one, so admins don't
// get shown extreme divisors like 1 or 1,475,000. Flip to true to enable.
// const RANGE_FILTER_ENABLED = false;
// const RANGE_PCT = 0.15;

/**
 * Find nearby TM counts that divide `amount` into an exact 2-decimal per-TM price.
 *
 * All arithmetic is done in integer PAISE. Doing it in rupees would reintroduce
 * the very floating-point error this component exists to avoid — 0.1 + 0.2 and
 * 14750 / 3000 * 3000 are both inexact in binary floating point, and a price that
 * fails to multiply back to the exact total is a payment-reconciliation bug.
 *
 * A candidate count is valid exactly when it divides amountPaise with no
 * remainder: amountPaise / count is then a whole number of paise, so
 * (amountPaise / count) / 100 is exact to 2 decimals and count × price === amount.
 *
 * Divisors are collected in O(√n): loop i up to √amountPaise and take both i and
 * amountPaise / i. For a ₹1,00,00,000 amount that is ~31.6k iterations rather
 * than the 1e9 a naive scan would need.
 *
 * @returns {{ isExact: boolean, currentPerTm: number|null, amountPaise: number,
 *             below: Array, above: Array }}
 */
export const suggestTmCounts = (
  amount,
  tmCount,
  { maxSuggestions = MAX_SUGGESTIONS } = {},
) => {
  const empty = {
    isExact: false,
    currentPerTm: null,
    amountPaise: 0,
    below: [],
    above: [],
  };

  const amt = Number(amount);
  const rawCount = Number(tmCount);

  // Guard every invalid shape: 0, negative, NaN, Infinity, null, "" …
  if (
    !Number.isFinite(amt) ||
    !Number.isFinite(rawCount) ||
    amt <= 0 ||
    rawCount <= 0
  ) {
    return empty;
  }

  const amountPaise = Math.round(amt * 100);
  const currentCount = Math.round(rawCount); // TM counts are whole units
  if (amountPaise <= 0 || currentCount <= 0) return empty;

  const isExact = amountPaise % currentCount === 0;
  const currentPerTm = amountPaise / currentCount / 100;

  const below = [];
  const above = [];
  const collect = (divisor) => {
    if (divisor < currentCount) below.push(divisor);
    else if (divisor > currentCount) above.push(divisor);
  };

  for (let i = 1; i * i <= amountPaise; i++) {
    if (amountPaise % i !== 0) continue;
    collect(i);
    const pair = amountPaise / i;
    if (pair !== i) collect(pair);
  }

  // Nearest to the current count first
  below.sort((a, b) => b - a);
  above.sort((a, b) => a - b);

  const toSuggestion = (count) => {
    const perTmPaise = amountPaise / count;
    return {
      tmCount: count,
      perTmPaise,
      perTm: perTmPaise / 100,
      diff: count - currentCount,
    };
  };

  // ── Optional ±15% range filter ────────────────────────────────────────────
  // Keeps only realistic neighbours instead of the extreme divisors.
  // const inRange = (count) =>
  //   Math.abs(count - currentCount) <= currentCount * RANGE_PCT;
  // const belowList = RANGE_FILTER_ENABLED ? below.filter(inRange) : below;
  // const aboveList = RANGE_FILTER_ENABLED ? above.filter(inRange) : above;

  return {
    isExact,
    currentPerTm,
    amountPaise,
    below: below.slice(0, maxSuggestions).map(toSuggestion),
    above: above.slice(0, maxSuggestions).map(toSuggestion),
  };
};

// ── Money formatting ────────────────────────────────────────────────────────
const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Repeating prices need more digits to be recognisable as "not exact"
const moneyPrecise = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

const signed = (n) => (n > 0 ? `+${n}` : `${n}`);

// ── Suggestion button ───────────────────────────────────────────────────────
const SuggestionButton = ({ suggestion, onClick }) => (
  <Tooltip
    title={`Set TM count to ${suggestion.tmCount.toLocaleString("en-IN")} — ₹${money(
      suggestion.perTm,
    )} × ${suggestion.tmCount.toLocaleString("en-IN")} is an exact total`}
  >
    <Button
      size="small"
      onClick={() => onClick(suggestion.tmCount, suggestion.perTm)}
      style={{
        width: "100%",
        textAlign: "left",
        height: "auto",
        padding: "4px 8px",
        marginBottom: 4,
        borderColor: "#b7eb8f",
        background: "#f6ffed",
        color: "#237804",
        fontSize: 12,
        lineHeight: 1.4,
        whiteSpace: "normal",
      }}
    >
      <span style={{ fontWeight: 700 }}>
        {suggestion.tmCount.toLocaleString("en-IN")} TM
      </span>
      <span style={{ color: "#8c8c8c", marginLeft: 4 }}>
        ({signed(suggestion.diff)})
      </span>
      <span style={{ margin: "0 4px", color: "#8c8c8c" }}>→</span>
      <span style={{ fontWeight: 700 }}>₹{money(suggestion.perTm)}/TM</span>
    </Button>
  </Tooltip>
);

const ColumnHeading = ({ icon, label }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 4,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      color: "#8c8c8c",
      marginBottom: 4,
    }}
  >
    {icon}
    {label}
  </div>
);

const TmCountSuggestion = ({ amount, tmCount, onSelect }) => {
  const { isExact, currentPerTm, below, above } = useMemo(
    () => suggestTmCounts(amount, tmCount),
    [amount, tmCount],
  );

  // Nothing meaningful to say without a usable amount and count
  if (currentPerTm === null) return null;

  const handleSelect = (newCount, perTm) => {
    if (typeof onSelect === "function") onSelect(newCount, perTm);
  };

  // ── Already exact: confirm and stop ───────────────────────────────────────
  if (isExact) {
    return (
      <div
        style={{
          marginTop: 6,
          padding: "6px 10px",
          borderRadius: 6,
          border: "1px solid #b7eb8f",
          background: "#f6ffed",
          color: "#237804",
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <CheckCircleOutlined style={{ color: "#52c41a" }} />
        <span>
          ✓ TM count{" "}
          <strong>{Math.round(Number(tmCount)).toLocaleString("en-IN")}</strong>{" "}
          gives an exact price of <strong>₹{money(currentPerTm)}</strong> per TM
        </span>
      </div>
    );
  }

  const hasSuggestions = below.length > 0 || above.length > 0;

  return (
    <div
      style={{
        marginTop: 6,
        padding: "8px 10px",
        borderRadius: 6,
        border: "1px solid #ffe58f",
        background: "#fffbe6",
        fontSize: 12,
      }}
    >
      {/* Warning */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "#ad6800",
          marginBottom: hasSuggestions ? 8 : 0,
        }}
      >
        <WarningOutlined style={{ color: "#faad14" }} />
        <span>
          ₹{money(amount)} ÷{" "}
          {Math.round(Number(tmCount)).toLocaleString("en-IN")} ={" "}
          <strong>₹{moneyPrecise(currentPerTm)}/TM</strong> — not an exact
          2-decimal price.
        </span>
      </div>

      {hasSuggestions ? (
        <div style={{ display: "flex", gap: 10 }}>
          {below.length > 0 && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <ColumnHeading
                icon={<ArrowDownOutlined style={{ fontSize: 10 }} />}
                label="Decrease"
              />
              {below.map((s) => (
                <SuggestionButton
                  key={s.tmCount}
                  suggestion={s}
                  onClick={handleSelect}
                />
              ))}
            </div>
          )}
          {above.length > 0 && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <ColumnHeading
                icon={<ArrowUpOutlined style={{ fontSize: 10 }} />}
                label="Increase"
              />
              {above.map((s) => (
                <SuggestionButton
                  key={s.tmCount}
                  suggestion={s}
                  onClick={handleSelect}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ color: "#8c8c8c", fontSize: 11 }}>
          No nearby TM count divides ₹{money(amount)} exactly — adjust the amount
          instead.
        </div>
      )}
    </div>
  );
};

export default TmCountSuggestion;
